/**
 * SOUND-826 — pure logic for the read-only CheckoutChamp routing section.
 *
 * Everything here works on CC items stored VERBATIM by sound-api (minus
 * `gatewayData`). CheckoutChamp mixes types freely — `"billerId": "9"` inside a
 * profile vs `9` on the gateway itself, `percentage` numbers but `priority`
 * strings, `enabled` as 1 / "1" — so nothing here trusts a type. Ids are
 * compared as `String(x)`, flags go through `toFlag`, numbers through
 * `toNumber`. The API deliberately does NOT normalise (Phase 2 compares a
 * change's expected `from` against the live value), so this is the layer that
 * has to.
 *
 * Lifted out of the pages because sound-app has no component-test harness
 * (SOUND-679); this file is the only level at which the section is testable.
 */
import { EMPTY_DISPLAY, displayValue } from "utilities/displayValue";

// Tab order is the spec's: Profiles is the default and comes first.
export const ROUTING_OBJECT_TYPES = [
  { key: "profiles", label: "Profiles" },
  { key: "cascades", label: "Cascades" },
  { key: "binMaps", label: "BIN routing" },
  { key: "binProfiles", label: "BIN blocks" },
  { key: "recycle", label: "Recycle billing" },
  { key: "gateways", label: "Gateways" },
];

export const DEFAULT_ROUTING_TAB = "profiles";

// The id field differs per object type (contract table).
const ID_FIELD = {
  gateways: "billerId",
  profiles: "id",
  cascades: "cascadeId",
  binMaps: "id",
  binProfiles: "binProfileId",
  recycle: "id",
};

export function objectTypeLabel(key) {
  const t = ROUTING_OBJECT_TYPES.find((o) => o.key === key);
  return t ? t.label : key;
}

export function isRoutingTab(key) {
  return ROUTING_OBJECT_TYPES.some((o) => o.key === key);
}

export function itemId(type, item) {
  const field = ID_FIELD[type];
  const v = field && item ? item[field] : undefined;
  return v === null || v === undefined ? "" : String(v);
}

// ---------------------------------------------------------------------------
// Value coercion
// ---------------------------------------------------------------------------

/** 1 / "1" / true / "true" → true; 0 / "0" / false / "false" → false; else null. */
export function toFlag(v) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "1" || s === "true" || s === "yes") return true;
    if (s === "0" || s === "false" || s === "no") return false;
  }
  return null;
}

/** A finite number from a number or numeric string, else null. "" is null, not 0. */
export function toNumber(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Display any CC value: em-dash for blank, never "[object Object]" crashes. */
export function formatValue(v) {
  return displayValue(v);
}

/** Yes / No / — for a CC flag in any of its spellings. */
export function formatFlag(v) {
  const f = toFlag(v);
  if (f === null) return EMPTY_DISPLAY;
  return f ? "Yes" : "No";
}

/** "85%" from 85 or "85"; — when blank or non-numeric. */
export function formatPercent(v) {
  const n = toNumber(v);
  if (n === null) return EMPTY_DISPLAY;
  return `${Number.isInteger(n) ? n : Number(n.toFixed(2))}%`;
}

/**
 * CC's "no gateway" is any of null, "", 0 or "0" — a bin map with no MID target
 * carries `billerId: 0`, a recycle profile with no final MID `finalBillerId: 0`.
 */
export function isBlankId(v) {
  if (v === null || v === undefined) return true;
  const s = String(v).trim();
  return s === "" || s === "0";
}

// ---------------------------------------------------------------------------
// MID label resolution (from the API's `_sound` annotation)
// ---------------------------------------------------------------------------

/**
 * Resolve a billerId on an item to something a human recognises.
 *
 * sound-api annotates every billerId-bearing item with
 * `item._sound = { mids: { "<billerId>": { midId, label } } }`. An unmatched
 * billerId simply has no entry, and then we fall back to CC's own gateway title
 * plus the billerId — never a blank cell.
 *
 * @returns {{ billerId: string, midId: string|null, label: string, title: string|null, matched: boolean } | null}
 *   null when the billerId is blank (no gateway).
 */
export function resolveMid(item, billerId, fallbackTitle) {
  if (isBlankId(billerId)) return null;
  const key = String(billerId).trim();
  const entry = item?._sound?.mids?.[key];
  const title =
    typeof fallbackTitle === "string" && fallbackTitle.trim() ? fallbackTitle.trim() : null;
  if (entry && entry.midId) {
    return {
      billerId: key,
      midId: String(entry.midId),
      label: entry.label ? String(entry.label) : title || `Gateway #${key}`,
      title,
      matched: true,
    };
  }
  return {
    billerId: key,
    midId: null,
    label: title ? `${title} · #${key}` : `Gateway #${key}`,
    title,
    matched: false,
  };
}

/** Every `_sound` MID label on an item — feeds search. */
function soundLabels(item) {
  const mids = item?._sound?.mids;
  if (!mids || typeof mids !== "object") return [];
  return Object.values(mids)
    .map((m) => (m && m.label ? String(m.label) : ""))
    .filter(Boolean);
}

// ---------------------------------------------------------------------------
// Gateway sets (profiles + cascades): ordering and percentage totals
// ---------------------------------------------------------------------------

/** A copy sorted by numeric priority; non-numeric priorities sink to the end. */
export function sortByPriority(gateways) {
  if (!Array.isArray(gateways)) return [];
  return gateways
    .map((g, i) => ({ g, i }))
    .sort((a, b) => {
      const pa = toNumber(a.g?.priority);
      const pb = toNumber(b.g?.priority);
      if (pa === null && pb === null) return a.i - b.i;
      if (pa === null) return 1;
      if (pb === null) return -1;
      return pa - pb || a.i - b.i;
    })
    .map(({ g }) => g);
}

/**
 * A gateway row with no `enabled` key at all (cascade gateways never carry one)
 * counts as enabled — it is in the set. Only an explicit 0 / "0" removes it.
 */
export function isGatewayEnabled(g) {
  if (!g || !Object.prototype.hasOwnProperty.call(g, "enabled")) return true;
  const f = toFlag(g.enabled);
  return f !== false;
}

/**
 * Percentage totals for a profile or cascade.
 *
 * `total` sums the ENABLED gateways only — a disabled gateway carries a
 * percentage but receives no traffic, so it does not count toward 100.
 * `flagged` is true only for a WEIGHTED set whose enabled percentages do not
 * sum to 100. PRIORITY sets legitimately carry 0s everywhere and are never
 * flagged. An empty enabled set is flagged too: a WEIGHTED profile with nothing
 * live routes nothing.
 */
export function percentageSummary(gateways, algorithm) {
  const list = Array.isArray(gateways) ? gateways : [];
  const weighted = String(algorithm || "").trim().toUpperCase() === "WEIGHTED";
  let total = 0;
  let allTotal = 0;
  let enabledCount = 0;
  for (const g of list) {
    const pct = toNumber(g?.percentage) || 0;
    allTotal += pct;
    if (isGatewayEnabled(g)) {
      enabledCount += 1;
      total += pct;
    }
  }
  total = Math.round(total * 100) / 100;
  allTotal = Math.round(allTotal * 100) / 100;
  const flagged = weighted && Math.abs(total - 100) > 0.001;
  return { total, allTotal, enabledCount, weighted, flagged };
}

/** The cascade a profile links to, by `cascadeId`, or null. */
export function linkedCascade(profile, cascades) {
  if (!profile || isBlankId(profile.cascadeId)) return null;
  const key = String(profile.cascadeId).trim();
  const found = (Array.isArray(cascades) ? cascades : []).find(
    (c) => String(c?.cascadeId) === key
  );
  return {
    cascadeId: key,
    title: found?.title ? String(found.title) : null,
    found: !!found,
  };
}

// ---------------------------------------------------------------------------
// BIN routing / BIN blocks
// ---------------------------------------------------------------------------

export function binRange(item) {
  const start = item?.startRange == null ? "" : String(item.startRange).trim();
  const end = item?.endRange == null ? "" : String(item.endRange).trim();
  if (!start && !end) return EMPTY_DISPLAY;
  if (!end || start === end) return start || end;
  if (!start) return end;
  return `${start}–${end}`;
}

/**
 * Does a typed digit string fall inside a BIN range? Lets an operator paste a
 * card's first digits and find the rule that catches it. Compares the leading
 * digits at the range's own width, so "51780512" hits 517805–517805.
 */
export function binInRange(digits, start, end) {
  if (!/^\d+$/.test(String(digits || ""))) return false;
  const s = start == null ? "" : String(start).trim();
  const e = end == null ? "" : String(end).trim() || s;
  if (!/^\d+$/.test(s) || !/^\d+$/.test(e)) return false;
  const width = s.length;
  if (String(digits).length < width) return false;
  const probe = Number(String(digits).slice(0, width));
  return probe >= Number(s) && probe <= Number(e);
}

/** Card-type restriction labels on a BIN map. */
export function binMapCardTypes(item) {
  const out = [];
  if (toFlag(item?.creditOnly)) out.push("Credit only");
  if (toFlag(item?.debitOnly)) out.push("Debit only");
  if (toFlag(item?.prepaidOnly)) out.push("Prepaid only");
  if (toFlag(item?.prepaid)) out.push("Prepaid");
  return out;
}

/**
 * Where a BIN map sends traffic: a MID (billerId) or a gateway profile
 * (profileId). CC writes `billerId: 0` when the map targets a profile instead.
 */
export function binMapTarget(item, profiles) {
  if (!isBlankId(item?.billerId)) {
    return { kind: "mid", mid: resolveMid(item, item.billerId, item.title) };
  }
  if (!isBlankId(item?.profileId)) {
    const key = String(item.profileId).trim();
    const p = (Array.isArray(profiles) ? profiles : []).find((x) => String(x?.id) === key);
    return {
      kind: "profile",
      profileId: key,
      label: p?.title ? `${p.title} · profile #${key}` : `Profile #${key}`,
    };
  }
  return { kind: "none" };
}

// ---------------------------------------------------------------------------
// Recycle billing
// ---------------------------------------------------------------------------

/** The nine interval / price-reduction steps, all nine, with `active` set. */
export function recycleSteps(item) {
  const steps = [];
  for (let i = 1; i <= 9; i += 1) {
    const interval = item?.[`intervalDays${i}`];
    const reduction = item?.[`priceReduction${i}`];
    steps.push({
      step: i,
      intervalDays: toNumber(interval),
      priceReduction: reduction === null || reduction === undefined ? null : reduction,
      active: toNumber(interval) !== null,
    });
  }
  return steps;
}

/**
 * The MIDs a recycle profile touches. Attempts 1–3 run on the order's original
 * MID (the attached ones); CC's `attemptBillerId1` covers attempts 4–6,
 * `attemptBillerId2` 7–9, then `finalBillerId`. A blank slot is null — the
 * attempt stays on the MID it was already on.
 */
export function recycleMids(item) {
  const attached = (Array.isArray(item?.attachedBillerIds) ? item.attachedBillerIds : [])
    .map((a) => resolveMid(item, a?.billerId, a?.title))
    .filter(Boolean);
  return {
    attached,
    attempts4to6: resolveMid(item, item?.attemptBillerId1, null),
    attempts7to9: resolveMid(item, item?.attemptBillerId2, null),
    final: resolveMid(item, item?.finalBillerId, null),
  };
}

// ---------------------------------------------------------------------------
// Gateways
// ---------------------------------------------------------------------------

const BRANDS = [
  ["visa", "Visa"],
  ["mastercard", "Mastercard"],
  ["amex", "Amex"],
  ["discover", "Discover"],
];

export function cardBrands(gateway) {
  return BRANDS.filter(([k]) => toFlag(gateway?.[k])).map(([, label]) => label);
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

function gatewayRowsText(gateways) {
  return (Array.isArray(gateways) ? gateways : []).flatMap((g) => [g?.title, g?.billerId]);
}

/** Every string a search on this object type should be able to hit. */
export function searchTextFor(type, item) {
  if (!item) return "";
  let parts;
  switch (type) {
    case "profiles":
      parts = [item.title, item.id, item.algorithm, item.limitType, ...gatewayRowsText(item.gateways)];
      break;
    case "cascades":
      parts = [item.title, item.cascadeId, item.algorithm, ...gatewayRowsText(item.gateways)];
      break;
    case "binMaps":
      parts = [
        item.name, item.startRange, item.endRange, item.bankNames, item.title,
        item.billerId, item.campaignName, item.profileId,
      ];
      break;
    case "binProfiles":
      parts = [item.binProfileId, item.startRange, item.endRange, item.blockMessage];
      break;
    case "recycle":
      parts = [
        item.id, item.orderType, item.attemptBillerId1, item.attemptBillerId2, item.finalBillerId,
        ...gatewayRowsText(item.attachedBillerIds),
      ];
      break;
    case "gateways":
      parts = [
        item.billerId, item.title, item.gatewayName, item.descriptor, item.midNumber,
        item.processorName, item.merchantCategoryName,
      ];
      break;
    default:
      parts = [];
  }
  return [...parts, ...soundLabels(item)]
    .filter((p) => p !== null && p !== undefined && p !== "")
    .map((p) => String(p).toLowerCase())
    .join(" \u0001 ");
}

/**
 * Case-insensitive, every whitespace-separated token must match. On the two BIN
 * tabs an all-digit token also matches by range, so "4111111" finds the map
 * covering 411111–411199 without anyone typing its exact start.
 */
export function filterItems(type, items, query) {
  const list = Array.isArray(items) ? items : [];
  const tokens = String(query || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return list;
  const binAware = type === "binMaps" || type === "binProfiles";
  return list.filter((item) => {
    const text = searchTextFor(type, item);
    return tokens.every(
      (t) =>
        text.includes(t) ||
        (binAware && /^\d+$/.test(t) && binInRange(t, item?.startRange, item?.endRange))
    );
  });
}

// ---------------------------------------------------------------------------
// Access state + snapshot metadata
// ---------------------------------------------------------------------------

/**
 * What one tab should render, from `snapshot.objects[type]`:
 *   ok      → items (possibly empty)
 *   denied  → CC said "API user does not have access to this endpoint"; a
 *             NORMAL state for most clients, not a failure
 *   missing → the snapshot holds nothing for this type at all
 */
export function accessState(snapshot, type) {
  const obj = snapshot?.objects?.[type];
  if (!obj || typeof obj !== "object") return { state: "missing", items: [] };
  if (obj.access === false) return { state: "denied", items: [], reason: obj.reason || null };
  return { state: "ok", items: Array.isArray(obj.items) ? obj.items : [] };
}

export function noAccessMessage(type) {
  return `No API access to ${objectTypeLabel(type)} — enable it on this client's CheckoutChamp API user`;
}

/** Labels of the object types a client's API user cannot read (`latest.access`). */
export function unreadableTypes(access) {
  if (!access || typeof access !== "object") return [];
  return ROUTING_OBJECT_TYPES.filter((t) => access[t.key] === false).map((t) => t.label);
}

/** "Changed outside Sound since the previous snapshot: Profiles, BIN routing", or null. */
export function changedBanner(changedSincePrevious) {
  const list = Array.isArray(changedSincePrevious) ? changedSincePrevious : [];
  if (!list.length) return null;
  // Spec order, not whatever order the API happened to emit.
  const known = ROUTING_OBJECT_TYPES.filter((t) => list.includes(t.key)).map((t) => t.label);
  const unknown = list.filter((k) => !isRoutingTab(k));
  return `Changed outside Sound since the previous snapshot: ${[...known, ...unknown].join(", ")}`;
}

/** Short "Profiles 3 · Gateways 9" summary from `counts`; unreadable (null) types are skipped. */
export function countsSummary(counts) {
  if (!counts || typeof counts !== "object") return "";
  return ROUTING_OBJECT_TYPES.filter((t) => typeof counts[t.key] === "number")
    .map((t) => `${t.label} ${counts[t.key]}`)
    .join(" · ");
}

/** A refresh failure as a sentence. 429 is the 60-second throttle, not an error. */
export function refreshErrorMessage(err) {
  if (err?.response?.status === 429) {
    return "This client was refreshed less than a minute ago. Try again shortly.";
  }
  return err?.response?.data?.error || err?.message || "Refresh failed.";
}

/** Gateway money fields worth showing, blanks dropped. CC sends them as strings. */
const FEE_FIELDS = [
  ["txnFee", "Txn fee"],
  ["discountRate", "Discount rate"],
  ["reserveAccountRate", "Reserve"],
  ["chargebackFee", "CB fee"],
  ["rdrFee", "RDR fee"],
  ["ethocaFee", "Ethoca fee"],
  ["monthlyFee", "Monthly fee"],
];

/** Non-zero fee/rate fields as `{ label, value }`. All-zero gateways return []. */
export function gatewayFees(gateway) {
  return FEE_FIELDS.filter(([k]) => {
    const n = toNumber(gateway?.[k]);
    return n !== null && n !== 0;
  }).map(([k, label]) => ({ label, value: String(gateway[k]) }));
}

/** "0.00" / 0 / blank all read as "No limit" — CC's zero monthly limit is uncapped. */
export function formatMonthlyLimit(v) {
  const n = toNumber(v);
  if (n === null || n === 0) return "No limit";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Absolute timestamp for the header; em-dash for blank or unparseable. */
export function formatTimestamp(v) {
  if (v === null || v === undefined || v === "") return EMPTY_DISPLAY;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return EMPTY_DISPLAY;
  return d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
